/**
 * Layout de l'Espace Formateur — supporte les modes clair et sombre.
 *
 * - Mode clair par défaut (charte 2i amber sur fond crème).
 * - Toggle dans le header.
 * - Cohérent avec l'accueil refondue.
 */
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, LogOut, Sun, Moon } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { logout as logoutAction } from '../store/slices/authSlice';
import { useFormateurTheme } from './useTheme';

interface Props {
  children: React.ReactNode;
  title?: string;
  showBack?: boolean;
}

export const FormateurLayout: React.FC<Props> = ({ children, title, showBack = true }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { isLight, toggle } = useFormateurTheme();

  const onAccueil = location.pathname === '/formateur' || location.pathname === '/formateur/accueil';
  const canBack = showBack && !onAccueil;

  const handleLogout = () => {
    if (!window.confirm('Se déconnecter ?')) return;
    dispatch(logoutAction());
    navigate('/login');
  };

  // Refonte 2026-09-02 : charte épurée façon Facebook (fond gris neutre au
  // lieu d'amber crème, header blanc pur, logo sans ring coloré). Le mode
  // sombre garde son fond gris-950 comme le portail étudiant.
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100 transition-colors">
      <header className="sticky top-0 z-30 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-3xl mx-auto px-3 py-2.5 flex items-center gap-2">
          {canBack ? (
            <button onClick={() => navigate(-1)}
              className="p-1.5 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition" aria-label="Retour">
              <ArrowLeft size={18} />
            </button>
          ) : (
            <img src="/logo.png" alt="2i"
              className="w-9 h-9 rounded-md bg-white flex-shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-gray-900 dark:text-white leading-tight truncate">
              {title || 'Espace Formateur'}
            </div>
            {!title && (
              <div className="text-[10px] text-gray-500 dark:text-gray-400 truncate">Institut International 2i</div>
            )}
          </div>
          <button onClick={toggle}
            className="p-1.5 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition"
            aria-label={isLight ? 'Mode sombre' : 'Mode clair'}
            title={isLight ? 'Mode sombre' : 'Mode clair'}>
            {isLight ? <Moon size={16} /> : <Sun size={16} />}
          </button>
          {onAccueil && (
            <button onClick={handleLogout}
              className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition"
              aria-label="Déconnexion" title="Déconnexion">
              <LogOut size={16} />
            </button>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-3 py-3 pb-24">
        {children}
      </main>
    </div>
  );
};
